import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { GOLD_THEME as G } from "../../components/shared/goldTheme";
import { GOLD_FIELD as F } from "../../components/shared/GoldField";

const brand = {
  primary: { main: "#2B37D4" },
  secondary: { main: "#E769A6" },
  error: { main: "#D72A2A" },
  warning: { main: "#FC7B09" },
  info: { main: "#6B7D6A" },
  success: { main: "#09FE00" },
};

const lightPalette = {
  mode: "light",
  ...brand,
  background: {
    default: "#F4F6F8",
    paper: "#FFFFFF",
  },
  text: {
    primary: "#000000",
    secondary: "#4B5563",
  },
};

const darkPalette = {
  mode: "dark",
  ...brand,
  background: {
    default: "#0f1115",
    paper: "#171a21",
  },
  text: {
    primary: "#e8e9ec",
    secondary: "#a3a8b4",
  },
};

/** @param {'light'|'dark'} mode */
export const getTheme = (mode) =>
  responsiveFontSizes(
    createTheme({
      palette: mode === "dark" ? darkPalette : lightPalette,
    })
  );

// ── Cream / gold theme (Task Manager forms) ──────────────────────────────────────────────────────────────────────
// The app-wide theme above is blue/pink. Pages that follow the cream/gold admin look (goldTheme.jsx / GoldField.jsx)
// wrap their content in <ThemeProvider theme={goldTheme}>, so every MUI control inside — buttons, fields, selects,
// tabs, switches, dialogs, drawers and menus (rendered in portals, still inside the provider's tree) — comes out gold
// without each file restyling its own controls. It extends the light theme above, so fonts and sizes stay the same.
// "!important" on the field borders and focus ring is deliberate: index.css paints a purple focus ring on every
// outlined input, and GoldField overrides it the same way.
const goldGradientButton = {
  background: G.gradient,
  color: "#fff",
  boxShadow: "0 4px 14px rgba(111,82,18,0.30)",
  "&:hover": { background: G.gradientHover, boxShadow: "0 8px 22px rgba(111,82,18,0.38)" },
  "&.Mui-disabled": { background: G.gradient, color: "rgba(255,255,255,0.85)", opacity: 0.5 },
};

const goldSurface = (extra = {}) => ({ background: G.surface, border: `1px solid ${G.border}`, color: G.text, ...extra });
const goldMenuPaper = goldSurface({ borderRadius: 10, boxShadow: "0 8px 28px rgba(122,87,18,0.14)" });

export const getGoldTheme = (base = getTheme("light")) =>
  responsiveFontSizes(
    createTheme(base, {
      palette: {
        mode: "light",
        primary: { main: G.goldDark, dark: "#5a420e", light: G.gold, contrastText: "#fff" },
        secondary: { main: G.gold },
        // The app theme's success green is a neon (#09FE00) that is unreadable on cream; chips, alerts and the Done
        // columns inside gold pages use this deeper green instead (same as the boards' Done colour).
        success: { main: "#2e7d32", dark: "#1b5e20", light: "#4caf50", contrastText: "#fff" },
        text: { primary: G.text, secondary: G.muted },
        background: { default: G.pageBg, paper: G.surface },
        divider: G.borderSoft,
      },
      shape: { borderRadius: 10 },
      components: {
        MuiButton: {
          styleOverrides: {
            root: { textTransform: "none", fontWeight: 700, borderRadius: 10 },
            containedPrimary: goldGradientButton,
            outlinedPrimary: {
              border: `1px solid ${G.pill}`, color: G.goldDark, background: G.field,
              "&:hover": { background: "#fdf1d8", borderColor: G.gold },
            },
            textPrimary: { color: G.goldDark, "&:hover": { background: G.goldSoft } },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              background: F.bg,
              borderRadius: 8,
              color: F.text,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: `${F.border} !important`, borderWidth: "1px !important" },
              "&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline": { borderColor: `${F.borderHover} !important` },
              "&.Mui-focused": { background: F.bgFocus, boxShadow: "0 0 0 3px rgba(184,137,44,0.15)" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${F.focus} !important` },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: `${F.error} !important` },
              "&.Mui-focused::before": { display: "none !important", animation: "none !important" },
              "&.Mui-disabled": { opacity: 0.75 },
            },
          },
        },
        MuiInputLabel: { styleOverrides: { root: { color: G.muted, "&.Mui-focused": { color: G.goldDark } } } },
        MuiDialog: {
          styleOverrides: {
            paper: goldSurface({
              borderRadius: 16, boxShadow: "0 24px 64px rgba(122,87,18,0.20)",
              "& .MuiDialogTitle-root": { fontWeight: 800 },
              "& .MuiDialogActions-root": { padding: "8px 24px 20px", gap: 8 },
            }),
          },
        },
        MuiDrawer: { styleOverrides: { paper: goldSurface({ boxShadow: "-12px 0 40px rgba(122,87,18,0.14)" }) } },
        MuiMenu: { styleOverrides: { paper: goldMenuPaper } },
        MuiPopover: { styleOverrides: { paper: goldMenuPaper } },
        MuiAutocomplete: { styleOverrides: { paper: goldMenuPaper } },
        MuiMenuItem: {
          styleOverrides: { root: { "&.Mui-selected": { background: "rgba(184,137,44,0.14)" }, "&:hover": { background: "rgba(184,137,44,0.08)" } } },
        },
        MuiPaper: { styleOverrides: { outlined: { borderColor: G.borderSoft, background: G.surface, borderRadius: 14 } } },
        MuiTabs: { styleOverrides: { indicator: { backgroundColor: G.gold, height: 3, borderRadius: 3 } } },
        MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 700, color: G.muted, "&.Mui-selected": { color: G.goldDark } } } },
        // Toggle groups (e.g. Mine / Shared with me / Everyone): a solid cream bar so the page's gold swoosh cannot show
        // through, dark readable labels, and a clear gold gradient on the selected choice.
        MuiToggleButtonGroup: {
          styleOverrides: {
            root: { background: G.field, borderRadius: 10, boxShadow: "0 1px 4px rgba(122,87,18,0.10)" },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              textTransform: "uppercase",
              fontWeight: 700,
              letterSpacing: "0.03em",
              color: G.textSub,
              background: G.field,
              border: `1px solid ${G.pill}`,
              "&:hover": { background: "#fdf1d8", color: G.text },
              "&.Mui-selected": { background: G.gradient, color: "#fff", borderColor: G.goldDark },
              "&.Mui-selected:hover": { background: G.gradientHover, color: "#fff" },
            },
          },
        },
        MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
        MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
      },
    })
  );

export const goldTheme = getGoldTheme();

// Default export kept for any straggling static import — always light, so
// nothing breaks if a file hasn't been switched to `useTheme()`/getTheme yet.
export default getTheme("light");
